<template>
  <div class="auction-listing-container">
    <b-loading is-full-page :active="loading" can-cancel></b-loading>

    <AppTitle expand-title titulo="Relatórios — Listagem de Licitações" />

    <section>
      <div class="container is-fluid auction-filters-container">
        <FiltrosLicitacoesPanel
          v-model="panelFilters"
          :persona="persona"
          :applied="filters"
          :loading="loading"
          @filter="applyFilters"
          @clear="clearFilters"
          @chip="applyFilters"
        />
      </div>
    </section>

    <section>
      <div class="container is-fluid auction-options-container">
        <Report-Options
          :columns.sync="columns"
          :display-expanded-description="displayExpandedDescription"
          @updateExpanded="value => (expandedDescription = value)"
        />
      </div>
    </section>

    <section>
      <div class="container is-fluid auction-table-container">
        <div v-if="!items.length" class="panel">
          <div class="panel-block empty-panel">
            <section class="section">
              <div class="content has-text-grey has-text-centered">
                <p>Nenhuma licitação encontrada.</p>
              </div>
            </section>
          </div>
        </div>

        <div v-if="items.length" class="panel">
          <div class="box panel-block">
            <Auction-Table
              ref="auction-table"
              :items="items"
              :columns="columns"
              :expanded-description="expandedDescription"
            />
          </div>
        </div>
      </div>

      <div class="container is-fluid pagination-container">
        <AppPagination
          :data-filters="paginationFilters"
          :has-margin="false"
          :is-state-u-r-l-enabled="false"
          :total="count"
          @change-page="changePage"
          @change-per-page="changePerPage"
        />
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { Printd } from 'printd';

import AppPagination from '@/components/AppPagination';
import AppTitle from '@/components/AppTitulo';
import FiltrosLicitacoesPanel from '@/components/Licitacoes/FiltrosLicitacoesPanel';
import AuctionTable from '@/views/Reports/AuctionTable';
import ReportOptions from '@/views/Reports/ReportOptions';
import { defaultLicitacoesFilters } from '@/utils/licitacoesFiltersQuery';

export default {
  name: 'AuctionListing',
  components: {
    AppPagination,
    AppTitle,
    AuctionTable,
    FiltrosLicitacoesPanel,
    ReportOptions,
  },
  data() {
    const columns = [
      { name: 'Comprador', display: false },
      { name: 'Processo', display: false },
      { name: 'Número', display: true },
      { name: 'Modalidade', display: false },
      { name: 'Tipo', display: false },
      { name: 'Disputa', display: false },
      { name: 'Negociação', display: false },
      { name: 'Edital', display: false },
      { name: 'Objeto', display: true },
      { name: 'Publicação', display: false },
      { name: 'Abertura', display: true },
      { name: 'Reabertura', display: false },
      { name: 'Situação', display: true },
      { name: 'Itens', display: false },
    ];

    return {
      columns,
      panelFilters: defaultLicitacoesFilters(),
      displayExpandedDescription: false,
      expandedDescription: false,
    };
  },
  watch: {
    columns: {
      deep: true,
      handler() {
        const itemActive = this.columns.some(column => column.name === 'Itens' && column.display);
        if (itemActive) {
          this.verifyExpandedDescription();
        }
      },
    },
  },
  computed: {
    ...mapState('companies', ['company']),
    ...mapState('licitacoesPesquisa', ['items', 'count', 'filters', 'loading']),
    persona() {
      if (this.company.buyer) return 'pregoeiro';
      if (this.company.seller) return 'licitante';
      return 'visitante';
    },
    paginationFilters() {
      return { page: this.filters.pagina, perPage: this.filters.porPagina };
    },
  },
  methods: {
    ...mapActions('licitacoesPesquisa', ['pesquisarLicitacoes']),
    setFilters(filters) {
      this.$store.commit('licitacoesPesquisa/setFilters', filters);
    },
    async buscarLicitacoes() {
      try {
        await this.pesquisarLicitacoes({ company: this.company });
      } catch (error) {
        this.$alerta('Erro ao buscar licitações', 'is-danger');
      }
    },
    applyFilters(filters) {
      const nextFilters = { ...filters, pagina: 1 };
      this.panelFilters = nextFilters;
      this.setFilters(nextFilters);
      return this.buscarLicitacoes();
    },
    clearFilters() {
      const defaults = defaultLicitacoesFilters();
      this.panelFilters = defaults;
      this.setFilters(defaults);
      return this.buscarLicitacoes();
    },
    changePage(pagina) {
      this.setFilters({ ...this.filters, pagina });
      this.panelFilters = { ...this.filters, pagina };
      return this.buscarLicitacoes();
    },
    changePerPage(porPagina) {
      this.setFilters({ ...this.filters, porPagina, pagina: 1 });
      this.panelFilters = { ...this.filters, porPagina, pagina: 1 };
      return this.buscarLicitacoes();
    },
    checkSeller() {
      if (this.company.seller) {
        this.columns[0].display = true;
      }
    },
    printOut() {
      const impression = new Printd();
      const table = this.$refs['auction-table'].$el.children[1].children[0];
      impression.print(table, [
        'a { color: black; text-decoration: none; }',
        'table { width: 100%; }',
        'td { text-align: center; }',
        'table, th, td { border: 1px solid black; border-collapse: collapse; font-size: 14px; }',
        '.auction-column { margin: 0 2px; }',
        this.expandedDescription
          ? ''
          : '.elipsis-description { max-width: 50ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;}',
        '.item-container { display: flex; flex-wrap: wrap; margin-bottom: 1rem }',
        '.item-content { margin: 0.5rem 0.5rem; min-width: 300px; text-align: justify; }',
      ]);
    },
    verifyExpandedDescription() {
      setTimeout(() => {
        const table = this.$refs['auction-table'];
        if (table) {
          const itemBox = table.$refs.description;
          const itemContainer = table.$refs['item-container'];

          if (!itemBox || !itemContainer) {
            this.displayExpandedDescription = false;
            return;
          }

          const boxes = Array.isArray(itemBox) ? itemBox : [itemBox];
          const containers = Array.isArray(itemContainer) ? itemContainer : [itemContainer];

          this.displayExpandedDescription = boxes.some(
            (box, index) =>
              box && containers[index] && box.scrollWidth >= containers[index].scrollWidth,
          );
        }
      }, 1);
    },
  },
  async created() {
    this.panelFilters = { ...this.filters };
    await this.buscarLicitacoes();
  },
  mounted() {
    this.checkSeller();
  },
};
</script>

<style lang="scss" scoped>
.auction-filters-container {
  margin-top: 3rem;
}

.auction-options-container {
  margin: 1rem 0;
}

.auction-table-container {
  margin-bottom: 2rem;
}

.empty-panel {
  justify-content: center !important;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 3rem;
}
</style>
